import { motion } from 'framer-motion';
import { ChevronRightIcon, HomeIcon } from 'lucide-react';
import { Fragment, memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { Button } from '@components';
import { ApiEndpoint, FormType, Translation, VariantType } from '@enums';
import { useAppDispatch, useBreadcrumbItems, useWafSummary } from '@hooks';
import { useCdnSummary, useFormContent } from '@store';

const fadeInVariants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.2,
      ease: 'easeOut',
    },
  },
};

const containerVariants = {
  animate: {
    transition: {
      staggerChildren: 0.03,
    },
  },
};

// Breadcrumb Item Component (Memoized for performance)
const BreadcrumbItemComponent = memo(
  ({
    item,
    index,
    isLastItem,
    handleClick,
  }: {
    item: {
      id: string;
      name: string;
      path: string;
    };
    index: number;
    isLastItem: boolean;
    handleClick: (path: string, index: number) => void;
  }) => {
    return (
      <motion.li
        className="flex items-center gap-x-3"
        data-testid={`breadcrumb-item-${index}`}
        variants={fadeInVariants}
        initial="initial"
        animate="animate"
      >
        <ChevronRightIcon className="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
        <motion.div whileHover={!isLastItem ? { opacity: 0.7 } : undefined} transition={{ duration: 0.1 }}>
          <Button
            onClick={() => handleClick(item.path, index)}
            withoutPadding
            rounded="rounded-none"
            variant={VariantType.Transparent}
            disabled={isLastItem}
            className={`text-sm ${
              isLastItem
                ? 'text-gray-400 font-medium'
                : 'font-normal text-gray-600 hover:font-medium hover:text-gray-500'
            } py-0.5`}
            aria-current={isLastItem ? 'page' : undefined}
          >
            {item.name}
          </Button>
        </motion.div>
      </motion.li>
    );
  },
);

BreadcrumbItemComponent.displayName = 'BreadcrumbItem';

export const NavigatorButton = (): JSX.Element => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const { t: translate } = useTranslation(Translation.Common);

  const cdnSummary = useCdnSummary();
  const wafSummary = useWafSummary();
  const workspace = useFormContent({ formType: FormType.Workspace, endpoint: ApiEndpoint.CONFIGURATION });

  const breadcrumbItems = useBreadcrumbItems(
    pathname,
    {
      cdnSource: cdnSummary,
      wafSource: wafSummary,
      workspaceSource: workspace,
    },
    translate,
  );

  const handleBreadCrumbItem = useCallback(
    (path: string, index: number): void => {
      navigate(path);
    },
    [navigate, pathname, dispatch],
  );

  const handleHomeClick = useCallback(() => {
    navigate('/');
  }, [navigate]);

  if (breadcrumbItems.length === 0) {
    return <Fragment />;
  }

  return (
    <motion.ol className="flex items-center space-x-3" variants={containerVariants} initial="initial" animate="animate">
      {/* Home icon */}
      <motion.li variants={fadeInVariants}>
        <motion.div whileHover={{ opacity: 0.7 }} transition={{ duration: 0.1 }}>
          <Button
            onClick={handleHomeClick}
            withoutPadding
            rounded="rounded-none"
            variant={VariantType.Transparent}
            className="text-gray-400 hover:text-gray-500"
            aria-label="Home"
          >
            <HomeIcon className="size-5" />
          </Button>
        </motion.div>
      </motion.li>

      {/* Breadcrumb items */}
      {breadcrumbItems.map((item, index) => (
        <BreadcrumbItemComponent
          key={`${item.path}-${index}`}
          item={item}
          index={index}
          isLastItem={index === breadcrumbItems.length - 1}
          handleClick={handleBreadCrumbItem}
        />
      ))}
    </motion.ol>
  );
};
